import React, { useState } from 'react';
import { LegalLayout } from '@/components/layouts/LegalLayout';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Cookie, Settings, Shield, BarChart3 } from 'lucide-react';

export const PolitiqueCookies: React.FC = () => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  const handleSavePreferences = () => {
    // Ici vous implémenterez la logique de sauvegarde des préférences
    // Cookie preferences saved
  };

  return (
    <LegalLayout>
      <div className="prose prose-slate dark:prose-invert max-w-none">
        <div className="flex items-center gap-3 mb-6">
          <h1 className="text-3xl font-bold">Politique de cookies</h1>
          <Badge variant="outline" className="text-xs">CNIL</Badge>
        </div>
        
        <p className="text-muted-foreground mb-8">
          Dernière mise à jour : [Date - À remplir]
        </p>

        {/* Bannière de consentement simulée */}
        <div className="bg-primary/10 border border-primary/20 p-4 rounded-lg mb-8">
          <div className="flex items-start gap-3">
            <Cookie className="w-5 h-5 mt-1 text-primary" />
            <div className="flex-1">
              <h3 className="font-semibold mb-2">🍪 Gestion des cookies</h3>
              <p className="text-sm mb-3">
                Nous utilisons des cookies pour améliorer votre expérience. 
                Vous pouvez personnaliser vos préférences ci-dessous.
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSavePreferences}>
                  Sauvegarder les préférences
                </Button>
                <Button size="sm" variant="outline">
                  Accepter tous
                </Button>
                <Button size="sm" variant="ghost">
                  Tout refuser
                </Button>
              </div>
            </div>
          </div>
        </div>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Qu'est-ce qu'un cookie ?</h2>
          <div className="bg-muted/30 p-4 rounded-lg">
            <p>
              Un cookie est un petit fichier texte déposé sur votre ordinateur lors de la visite d'un site web. 
              Il permet de conserver des informations sur votre navigation et de vous reconnaître lors de vos prochaines visites.
            </p>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Types de cookies utilisés</h2>
          
          <div className="space-y-6">
            {/* Cookies techniques */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold">Cookies techniques (obligatoires)</h3>
                  <Badge variant="outline" className="text-xs">Toujours actifs</Badge>
                </div>
              </div>
              
              <p className="text-sm mb-3">
                Ces cookies sont nécessaires au fonctionnement du site et ne peuvent pas être désactivés.
              </p>
              
              <div className="bg-green-50 dark:bg-green-950/20 p-3 rounded">
                <h4 className="font-medium text-sm mb-2">Cookies inclus :</h4>
                <ul className="text-xs space-y-1">
                  <li>• <strong>session_id</strong> - Maintien de la session utilisateur</li>
                  <li>• <strong>auth_token</strong> - Authentification sécurisée</li>
                  <li>• <strong>csrf_token</strong> - Protection contre les attaques CSRF</li>
                  <li>• <strong>language_pref</strong> - Préférence de langue</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-2">
                  <strong>Durée :</strong> Session ou 30 jours maximum
                </p>
              </div>
            </div>

            {/* Cookies analytiques */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold">Cookies analytiques</h3>
                </div>
                <Switch 
                  checked={analyticsEnabled} 
                  onCheckedChange={setAnalyticsEnabled}
                />
              </div>
              
              <p className="text-sm mb-3">
                Ces cookies nous aident à comprendre comment vous utilisez notre site pour l'améliorer.
              </p>
              
              <div className={`p-3 rounded transition-opacity ${analyticsEnabled ? 'bg-blue-50 dark:bg-blue-950/20' : 'bg-muted/30 opacity-60'}`}>
                <h4 className="font-medium text-sm mb-2">Cookies inclus :</h4>
                <ul className="text-xs space-y-1">
                  <li>• <strong>_ga</strong> - Google Analytics (identifiant unique)</li>
                  <li>• <strong>_ga_[ID]</strong> - Google Analytics 4</li>
                  <li>• <strong>analytics_consent</strong> - Consentement analytiques</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-2">
                  <strong>Durée :</strong> 2 ans | <strong>Fournisseur :</strong> Google
                </p>
              </div>
            </div>

            {/* Cookies marketing */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold">Cookies marketing</h3>
                </div>
                <Switch 
                  checked={marketingEnabled} 
                  onCheckedChange={setMarketingEnabled}
                />
              </div>
              
              <p className="text-sm mb-3">
                Ces cookies permettent de vous proposer des publicités personnalisées.
              </p>
              
              <div className={`p-3 rounded transition-opacity ${marketingEnabled ? 'bg-purple-50 dark:bg-purple-950/20' : 'bg-muted/30 opacity-60'}`}>
                <h4 className="font-medium text-sm mb-2">Cookies inclus :</h4>
                <ul className="text-xs space-y-1">
                  <li>• <strong>_fbp</strong> - Facebook Pixel</li>
                  <li>• <strong>marketing_consent</strong> - Consentement marketing</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-2">
                  <strong>Durée :</strong> 90 jours | <strong>Fournisseur :</strong> Meta
                </p>
              </div>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Vos droits et contrôles</h2>
          
          <div className="grid gap-4">
            <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">🔧 Paramétrage de votre navigateur</h3>
              <p className="text-sm mb-2">
                Vous pouvez configurer votre navigateur pour gérer les cookies :
              </p>
              <ul className="text-sm space-y-1">
                <li>• <strong>Chrome :</strong> Paramètres → Confidentialité → Cookies</li>
                <li>• <strong>Firefox :</strong> Options → Vie privée → Cookies</li>
                <li>• <strong>Safari :</strong> Préférences → Confidentialité</li>
                <li>• <strong>Edge :</strong> Paramètres → Cookies et autorisations</li>
              </ul>
            </div>

            <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">⚠️ Attention</h3>
              <p className="text-sm">
                La désactivation de certains cookies peut affecter le fonctionnement normal 
                du site et limiter l'accès à certaines fonctionnalités.
              </p>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Conformité CNIL</h2>
          
          <div className="bg-muted/30 p-4 rounded-lg space-y-3">
            <p className="text-sm">
              <strong>Base légale :</strong> Consentement (article 82 de la loi Informatique et Libertés)
            </p>
            
            <p className="text-sm">
              <strong>Durée de conservation du consentement :</strong> 13 mois maximum
            </p>
            
            <p className="text-sm">
              <strong>Renouvellement :</strong> Le consentement doit être renouvelé périodiquement
            </p>
            
            <p className="text-sm">
              <strong>Retrait :</strong> Vous pouvez retirer votre consentement à tout moment 
              via les paramètres de cookies ou en nous contactant.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">Contact</h2>
          <div className="bg-primary/10 p-4 rounded-lg">
            <p className="text-sm">
              Pour toute question concernant notre politique de cookies :
            </p>
            <p className="text-sm mt-2">
              <strong>Email :</strong> [dpo@votredomaine.com - À remplir]<br/>
              <strong>Adresse :</strong> [Adresse postale - À remplir]
            </p>
          </div>
        </section>
      </div>
    </LegalLayout>
  );
};